/**
 * Helper functions for working with project date data
 * These functions provide utilities for date maps, payment calculations, and related operations
 * Used by DateBuilder, ExistingDebtHelper, and other components that need date processing
 */

import { TIME_PERIODS } from '~/config/financial-constants';
import type {
  DateInputs,
  DateRecord,
  DateRecordsMapByPaymentDate,
  DateRecordsMapByTaxYear,
  InputProjectPaymentInputs,
  PaymentDateForm,
  ProjectDateRecordByYear,
} from '~/types';
import { addYears, compareDates } from '~/utils/date-utils';

/**
 * Construct a payment date key for efficient lookups in dateRecordsMapByPaymentDate
 * Uses YYYYMMDD format as integer to match DateBuilder implementation
 *
 * @param paymentDate - Date string in YYYY-MM-DD format
 * @returns Integer key in YYYYMMDD format
 */
export function constructPaymentDateKey(paymentDate: string): number {
  const date = new Date(paymentDate);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return parseInt(`${year}${month}${day}`, 10);
}

/**
 * Parse a payment date key back to a date string
 *
 * @param key - Integer key in YYYYMMDD format
 * @returns Date string in YYYY-MM-DD format
 */
export function parsePaymentDateKey(key: number): string {
  const keyStr = key.toString();
  if (keyStr.length !== 8) {
    throw new Error(`Invalid payment date key: ${key}`);
  }

  const year = keyStr.substring(0, 4);
  const month = keyStr.substring(4, 6);
  const day = keyStr.substring(6, 8);

  return `${year}-${month}-${day}`;
}

/**
 * Get all payment dates for a specific tax year from date records
 *
 * @param dateRecordsMapByTaxYear - Map of tax years to date records
 * @param taxYear - The tax year to get payment dates for
 * @returns Array of payment date strings
 */
export function getPaymentDatesForTaxYear(
  dateRecordsMapByTaxYear: DateRecordsMapByTaxYear,
  taxYear: number
): string[] {
  const record = dateRecordsMapByTaxYear[taxYear];
  return record?.paymentDates || [];
}

/**
 * Check if a payment date belongs to a specific fiscal year
 *
 * @param paymentDate - The payment date to check
 * @param fiscalYearEndDate - The fiscal year end date
 * @returns Boolean indicating if payment date is in the fiscal year
 */
export function isPaymentInFiscalYear(paymentDate: string, fiscalYearEndDate: string): boolean {
  // Simple string comparison works for YYYY-MM-DD format
  return paymentDate <= fiscalYearEndDate;
}

/**
 * Get the tax year for a given payment date using the payment date map
 *
 * @param paymentDate - The payment date to lookup
 * @param dateRecordsMapByPaymentDate - Map of payment dates to their records
 * @returns The tax year or null if not found
 */
export function getTaxYearForPaymentDate(
  paymentDate: string,
  dateRecordsMapByPaymentDate: DateRecordsMapByPaymentDate
): number | null {
  const key = constructPaymentDateKey(paymentDate);
  const record = dateRecordsMapByPaymentDate[key];
  return record?.taxYear || null;
}

// =============================================================================
// DATE CONVERSION AND VALIDATION UTILITIES
// =============================================================================

/**
 * Converts MM/DD/YYYY format to YYYY-MM-DD format (engine format)
 *
 * @param dateString - Date in MM/DD/YYYY format
 * @returns Date in YYYY-MM-DD format or empty string if invalid
 */
export function convertToEngineDate(dateString: string): string {
  if (!dateString) return '';

  // Convert MM/DD/YYYY to YYYY-MM-DD
  const mmddyyyy = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/(\d{4})$/;
  const match = dateString.match(mmddyyyy);

  if (match) {
    const [, month, day, year] = match;
    return `${year}-${month}-${day}`;
  }

  // Already in YYYY-MM-DD format
  return dateString;
}

// =============================================================================
// PAYMENT DATE LOGIC UTILITIES
// =============================================================================

/**
 * Determines if a payment is the last payment in a fiscal year
 * For Winter tax schedule: November is the last payment (second payment)
 * For Summer tax schedule: May is the last payment (second payment)
 *
 * @param payment - The payment to check
 * @param allPayments - All payments in the year
 * @param fiscalYearEnd - The fiscal year end date
 * @param taxCollectionSchedule - Tax collection schedule ("summer" | "winter")
 * @returns True if this is the last payment in the fiscal year
 */
export function determineIsLastPayment(
  payment: PaymentDateForm,
  allPayments: PaymentDateForm[],
  fiscalYearEnd: string,
  taxCollectionSchedule?: 'summer' | 'winter'
): boolean {
  if (!payment.date || !fiscalYearEnd) return false;

  // For Winter tax schedule, find November payment (second payment)
  // For Summer tax schedule, find May payment (second payment)
  if (taxCollectionSchedule === 'winter') {
    // Find November payment (month 11)
    const novemberPayments = allPayments.filter((p) => {
      if (!p.date) return false;
      const month = parseInt(p.date.substring(5, 7), 10);
      return month === 11;
    });
    if (novemberPayments.length > 0) {
      return novemberPayments[0].date === payment.date;
    }
  } else if (taxCollectionSchedule === 'summer') {
    // Find May payment (month 5)
    const mayPayments = allPayments.filter((p) => {
      if (!p.date) return false;
      const month = parseInt(p.date.substring(5, 7), 10);
      return month === 5;
    });
    if (mayPayments.length > 0) {
      return mayPayments[0].date === payment.date;
    }
  }

  // Fallback: Find all payments within the fiscal year using string comparison
  const fiscalYearPayments = allPayments
    .filter((p) => p.date && compareDates(p.date, fiscalYearEnd) <= 0)
    .sort((a, b) => compareDates(a.date, b.date));

  // Check if this is the last payment in the fiscal year
  return (
    fiscalYearPayments.length > 0 &&
    fiscalYearPayments[fiscalYearPayments.length - 1].date === payment.date
  );
}

/**
 * Finds the last payment date within a fiscal year
 * For Winter tax schedule: November is the last payment (second payment)
 * For Summer tax schedule: May is the last payment (second payment)
 *
 * @param paymentDates - Array of payment date forms
 * @param fiscalYearEnd - The fiscal year end date
 * @param taxCollectionSchedule - Tax collection schedule ("summer" | "winter")
 * @returns The last payment date in the fiscal year or empty string
 */
export function findLastPaymentInFiscalYear(
  paymentDates: PaymentDateForm[],
  fiscalYearEnd: string,
  taxCollectionSchedule?: 'summer' | 'winter'
): string {
  if (!fiscalYearEnd || paymentDates.length === 0) return '';

  // For Winter tax schedule, find November payment (month 11) - this is the last payment
  if (taxCollectionSchedule === 'winter') {
    const novemberPayments = paymentDates.filter((p) => {
      if (!p.date) return false;
      const month = parseInt(p.date.substring(5, 7), 10);
      return month === 11;
    });
    if (novemberPayments.length > 0) {
      return novemberPayments[0].date;
    }
  }

  // For Summer tax schedule, find May payment (month 5) - this is the last payment
  if (taxCollectionSchedule === 'summer') {
    const mayPayments = paymentDates.filter((p) => {
      if (!p.date) return false;
      const month = parseInt(p.date.substring(5, 7), 10);
      return month === 5;
    });
    if (mayPayments.length > 0) {
      return mayPayments[0].date;
    }
  }

  // Fallback: Find all payments within the fiscal year using string comparison
  const validPayments = paymentDates
    .filter((p) => p.date && compareDates(p.date, fiscalYearEnd) <= 0)
    .sort((a, b) => compareDates(a.date, b.date));

  return validPayments.length > 0 ? validPayments[validPayments.length - 1].date : '';
}

// =============================================================================
// DATE RECORD MAP BUILDERS
// =============================================================================

/**
 * Build DateRecordsMapByTaxYear from project date records
 *
 * @param projectDateRecords - Array of project date records
 * @returns Map of tax year to date record
 */
export function buildDateRecordsMapByTaxYear(
  projectDateRecords: ProjectDateRecordByYear[]
): DateRecordsMapByTaxYear {
  const dateRecordsMapByTaxYear: DateRecordsMapByTaxYear = {};

  for (const record of projectDateRecords) {
    const taxYear = record.taxYear;
    if (!dateRecordsMapByTaxYear[taxYear]) {
      dateRecordsMapByTaxYear[taxYear] = record;
    }
  }

  return dateRecordsMapByTaxYear;
}

/**
 * Build DateRecordsMapByPaymentDate from project date records and date inputs
 * This creates the critical map that contains revenueCollectionByDatePercentage
 *
 * @param projectDateRecords - Array of project date records
 * @param dateInputs - Engine format date inputs with payment percentages
 * @returns Map of payment date keys to payment date records
 */
export function buildDateRecordsMapByPaymentDate(
  projectDateRecords: ProjectDateRecordByYear[],
  dateInputs: DateInputs // DateInputs type
): DateRecordsMapByPaymentDate {
  const dateRecordsMapByPaymentDate: DateRecordsMapByPaymentDate = {};

  // Create lookup maps from original date inputs
  const revenueCollectionByMonth = new Map<number, number>();
  const isInterestOnlyByMonth = new Map<number, boolean>();
  const isLastPaymentByMonth = new Map<number, boolean>();

  dateInputs.inputPaymentDateRecords.forEach((record: InputProjectPaymentInputs) => {
    const paymentDate = new Date(record.paymentDate);
    const month = paymentDate.getMonth() + 1; // 1-based month

    revenueCollectionByMonth.set(month, record.revenueCollectionByDatePercentage);
    isInterestOnlyByMonth.set(month, record.isInterestOnly);
    isLastPaymentByMonth.set(month, record.isLastPaymentInFiscalYear);
  });

  // Process each project date record
  for (const record of projectDateRecords) {
    for (const paymentDate of record.paymentDates) {
      const paymentDateKey = constructPaymentDateKey(paymentDate);

      if (!dateRecordsMapByPaymentDate[paymentDateKey]) {
        const paymentDateObj = new Date(paymentDate);
        const month = paymentDateObj.getMonth() + 1; // 1-based month

        dateRecordsMapByPaymentDate[paymentDateKey] = {
          paymentDate,
          taxYear: record.taxYear,
          fiscalYearEndDate: record.fiscalYearEndDate,
          isInterestOnly: isInterestOnlyByMonth.get(month) ?? false,
          isLastPaymentInFiscalYear: isLastPaymentByMonth.get(month) ?? false,
          revenueCollectionByDatePercentage: revenueCollectionByMonth.get(month) ?? 0,
        };
      }
    }
  }

  return dateRecordsMapByPaymentDate;
}

// =============================================================================
// PROJECT DATE RECORD BUILDERS
// =============================================================================

/**
 * Build project date records for the full project timeline
 * Projects payment dates and fiscal year information across all years
 *
 * @param initialTaxYear - The starting tax year
 * @param initialPaymentDates - Payment dates for the first year
 * @param firstFiscalYearEnd - Fiscal year end date for the first year
 * @param lastPaymentOfFirstFiscalYear - Last payment date of the first fiscal year
 * @returns Array of project date records for the standard project duration
 */
export function buildProjectDateRecords(
  initialTaxYear: number,
  initialPaymentDates: string[],
  firstFiscalYearEnd: string,
  lastPaymentOfFirstFiscalYear: string
): ProjectDateRecordByYear[] {
  const projectDateRecords: ProjectDateRecordByYear[] = [];

  for (let i = 0; i < TIME_PERIODS.PROJECT_YEARS; i++) {
    const currentTaxYear = initialTaxYear + i;

    // Calculate payment dates for this year (add i years to each initial payment date)
    const currentPaymentDates = initialPaymentDates.map((date) => {
      return addYears(date, i);
    });

    // Calculate fiscal year end date for this year
    const currentFiscalYearEndDate = addYears(firstFiscalYearEnd, i);

    // Calculate last payment date for this year
    const currentLastPaymentDate = addYears(lastPaymentOfFirstFiscalYear, i);

    projectDateRecords.push({
      projectYear: i + 1,
      taxYear: currentTaxYear,
      paymentDates: currentPaymentDates,
      fiscalYearEndDate: currentFiscalYearEndDate,
      lastPaymentDateOfFiscalYear: currentLastPaymentDate,
    });
  }

  return projectDateRecords;
}

// =============================================================================
// SUMMARY AND ANALYSIS UTILITIES
// =============================================================================

/**
 * Build a summary of date records for display
 *
 * @param data - Array of date records
 * @returns Summary object or null if no data
 */
export function buildDateSummary(data: DateRecord[]) {
  if (data.length === 0) return null;

  const firstYear = data[0];
  const lastYear = data[data.length - 1];

  return {
    startingTaxYear: firstYear.taxYear,
    endingTaxYear: lastYear.taxYear,
    yearSpan: data.length,
    paymentDatesPerYear: firstYear.paymentDates.length,
    fiscalYearPattern: firstYear.fiscalYearEndDate,
  };
}
